/**
 * Human-friendly formatting for the `LiveTrackAction.update_fields` payload.
 *
 * `update_fields` is an opaque dictionary built by the matcher / rollback /
 * deal-sync services (see {@link App\DTO\LiveTrackMatchDTO} and friends). The
 * History Inspector card surfaces it as a popover, and this module turns the
 * raw key/value bag into an ordered list of `{ label, value }` rows with:
 *  - curated labels for the keys we know about (case-sensitive),
 *  - generic snake-case → Title Case fallback for anything new,
 *  - value formatting for nested `{name}` objects, durations, status ids, etc.
 *
 * Elasticsearch may hand `update_fields` back as a JSON-encoded string rather
 * than a parsed object (the searchable model JSON-encodes it before indexing
 * — see {@link App\Models\Searchable\LiveTrackAction::toSearchableArray}), so
 * the parser tolerates both.
 */
import { displayStringForTrackMatchStatusId } from '@/constants/trackMatchStatus';
import { booleanDisplayString } from '@/utils/booleanDisplay';
import { columnHeaderLabel } from '@/utils/columnLabels';

/**
 * Curated labels for every known `update_fields` key. Keep this in sync with the
 * writers in the backend (LiveTrackMatchDTO::toArray, LiveTrackRollbackService,
 * LiveTrackDealSyncService, LiveTrackRawDdexLinkService) — when a new key starts
 * showing up the generic fallback covers it readably until a curated label is
 * added.
 */
const KEY_LABELS: Record<string, string> = {
  name: 'Title',
  remix_name: 'Remix',
  isrc: 'ISRC',
  bitrate: 'Bitrate',
  duration: 'Duration',
  album: 'Album',
  artist: 'Artist',
  album_artist: 'Album Artist',
  catalog_code: 'Catalog Code',
  copyright: 'Copyright',
  genre: 'Genre',
  record_label: 'Record Label',
  raw_ddex_record_label_name: 'Record Label',
  raw_ddex_root_record_label_name: 'Root Record Label',
  raw_ddex_root_record_label_party_id: 'Root Record Label Party',
  raw_ddex_track_id: 'DDEX Track',
  track_match_status_id: 'Match Status',
};

/**
 * Labels for which keys we want to show *last* — they are housekeeping refs
 * rather than the music-metadata the user reads first. Anything not in the
 * ordering map keeps its insertion order from the payload.
 */
const ORDERING: Record<string, number> = {
  name: 1,
  remix_name: 2,
  artist: 3,
  album_artist: 4,
  album: 5,
  isrc: 6,
  duration: 7,
  bitrate: 8,
  catalog_code: 9,
  copyright: 10,
  genre: 11,
  record_label: 12,
  raw_ddex_record_label_name: 13,
  raw_ddex_root_record_label_name: 14,
  raw_ddex_root_record_label_party_id: 15,
  raw_ddex_track_id: 90,
  track_match_status_id: 91,
};

const NULL_VALUE_PLACEHOLDER = '—';

/**
 * Parse the raw `update_fields` value off an action. Returns null when the
 * payload is missing, empty, or unparseable JSON — callers should treat that
 * the same as "nothing to show". An empty object also returns null so the
 * popover trigger can be hidden for actions that carry no field changes.
 */
export function parseUpdateFields(raw: unknown): Record<string, unknown> | null {
  if (raw == null) {
    return null;
  }
  let value: unknown = raw;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === '[]' || trimmed === '{}' || trimmed === 'null') {
      return null;
    }
    try {
      value = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  if (Array.isArray(value)) {
    // The model casts `update_fields` as `array`; an empty array represents
    // "no fields" and PHP serializes an empty associative-array as `[]`.
    if (value.length === 0) {
      return null;
    }
    // Defensive: a list-of-objects probably means a misformed payload — flatten
    // it into one record so the popover still renders rather than throwing.
    const merged: Record<string, unknown> = {};
    for (const item of value) {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        Object.assign(merged, item);
      }
    }
    return Object.keys(merged).length > 0 ? merged : null;
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return Object.keys(record).length > 0 ? record : null;
  }
  return null;
}

export interface UpdateFieldEntry {
  /** Raw payload key (used for the `:key` in v-for). */
  key: string;
  /** Curated label (e.g. "Title") or snake-to-title fallback. */
  label: string;
  /** Human-friendly value string. Always non-empty — null/undefined render as "—". */
  value: string;
}

/**
 * Format the parsed `update_fields` record into an ordered list of human
 * entries. Known keys appear in a curated order ({@link ORDERING}); unknown
 * keys keep their payload order after the known ones.
 */
export function formatUpdateFieldsEntries(
  parsed: Record<string, unknown> | null,
): UpdateFieldEntry[] {
  if (parsed == null) {
    return [];
  }
  const entries = Object.entries(parsed).map(([key, value], index) => ({
    key,
    // columnHeaderLabel, not snakeToTitle: this pane builds its labels from the
    // raw payload keys rather than from models.json columns, so it is not covered
    // by the entityColumns change and would still read "Is Archived" here while
    // every other surface reads "Archived".
    label: KEY_LABELS[key] ?? columnHeaderLabel(key),
    value: formatValueForKey(key, value),
    order: ORDERING[key] ?? 50 + index,
  }));
  entries.sort((a, b) => a.order - b.order);
  return entries.map(({ key, label, value }) => ({ key, label, value }));
}

function formatValueForKey(key: string, value: unknown): string {
  if (value == null) {
    return NULL_VALUE_PLACEHOLDER;
  }
  if (key === 'track_match_status_id') {
    return displayStringForTrackMatchStatusId(value);
  }
  if (key === 'duration' && typeof value === 'number' && Number.isFinite(value)) {
    return formatDurationSeconds(value);
  }
  if (key === 'bitrate' && typeof value === 'number' && Number.isFinite(value)) {
    return `${value} kbps`;
  }
  if (key === 'copyright' && typeof value === 'number' && Number.isFinite(value)) {
    return value > 0 ? `©${value}` : NULL_VALUE_PLACEHOLDER;
  }
  return formatGenericValue(value);
}

/**
 * Generic value formatter — used for any key without a key-specific rule.
 * Nested `{name}` / `{display_string}` objects collapse to their name so the
 * popover stays one-line-per-field.
 */
function formatGenericValue(value: unknown): string {
  if (value == null) {
    return NULL_VALUE_PLACEHOLDER;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? NULL_VALUE_PLACEHOLDER : trimmed;
  }
  if (typeof value === 'boolean') {
    return booleanDisplayString(value);
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? NULL_VALUE_PLACEHOLDER : value.map(formatGenericValue).join(', ');
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const name =
      (typeof record.name === 'string' && record.name) ||
      (typeof record.display_string === 'string' && record.display_string) ||
      (typeof record.title === 'string' && record.title);
    if (name) {
      return String(name);
    }
    // Last resort: pretty-print a small object as `key: value, …`.
    return Object.entries(record)
      .map(([k, v]) => `${k}: ${formatGenericValue(v)}`)
      .join(', ');
  }
  return String(value);
}

function formatDurationSeconds(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return NULL_VALUE_PLACEHOLDER;
  }
  const whole = Math.round(totalSeconds);
  const minutes = Math.floor(whole / 60);
  const seconds = whole % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Pull the album name out of `update_fields` — the matcher's
 * {@link App\DTO\LiveTrackMatchDTO} stores it as a `{ id, name }` (or similar)
 * nested object under the `album` key. Returns null when there's no usable
 * album so callers can fall back to the nested `live_track.album` reference.
 */
export function readUpdateFieldsAlbumName(raw: unknown): string | null {
  const parsed = parseUpdateFields(raw);
  if (parsed == null) {
    return null;
  }
  const album = parsed.album;
  if (album && typeof album === 'object' && !Array.isArray(album)) {
    const record = album as Record<string, unknown>;
    const name =
      (typeof record.name === 'string' && record.name.trim()) ||
      (typeof record.display_string === 'string' && record.display_string.trim());
    return name || null;
  }
  if (typeof album === 'string') {
    const trimmed = album.trim();
    return trimmed === '' ? null : trimmed;
  }
  return null;
}
